import React, { useCallback, useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { IoMdDownload } from "react-icons/io";
import { FiXCircle } from "react-icons/fi";
import DateRangePicker from "../../molecules/DateRangePicker";
import {
  getCancelreasonAnalytics,
  downloadCancelReasonAnalytics,
} from "../../../lib/apis/analytics";
import { useHospDocData } from "../../../lib/contexts/HospitalDoctorContext";
import {
  CacheWithExpiration,
  getDateBeforeXDays,
  getISODateFromDate,
} from "../../../lib/utils/funcs";
import {
  CancelReasonAnalyticsResponse,
  DownloadRequestResponse,
} from "../../../lib/utils/types";
import ChartLoader from "../../molecules/ChartLoader";
import ChartError from "../../molecules/ChartError";
import NoDataState from "../../molecules/NoDataState";
import { format } from 'date-fns';
import { useUserData } from "../../../lib/contexts/UserContext";
import { toast } from "react-toastify";

interface CancelledReasonGraphProps {
  hospitalId?: string;
  startDate?: string;
  endDate?: string;
  report?:boolean;
  cancelStats?: any;
  onDownloadRequested?: (response: DownloadRequestResponse) => void;
}

function CancelledReasonGraph({ hospitalId, startDate, endDate, report, cancelStats, onDownloadRequested }: CancelledReasonGraphProps) {
  // Use prop if provided, else fallback to context and internal state
  const { userData } = useUserData();
  const { hospitalID: contextHospitalID } = useHospDocData();
  const effectiveHospitalId = hospitalId || contextHospitalID;
  // If props provided, use them, else use internal state
  const [internalStartDate, setInternalStartDate] = useState<Date>(getDateBeforeXDays(30));
  const [internalEndDate, setInternalEndDate] = useState<Date>(getDateBeforeXDays(1));
  const effectiveStartDate = startDate ? new Date(startDate) : internalStartDate;
  const effectiveEndDate = endDate ? new Date(endDate) : internalEndDate;
  const [cancelReasonData, setCancelReasonData] = useState<CancelReasonAnalyticsResponse>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Update handlers to use internal state only if not in external mode
  const isExternal = !!(hospitalId && startDate && endDate);
  const handleStartDateChange = (date: Date | null) => {
    if (!isExternal && date) setInternalStartDate(date);
  };
  const handleEndDateChange = (date: Date | null) => {
    if (!isExternal && date) setInternalEndDate(date);
  };

  const handleRetry = () => {
    fetchCancelReasonDataWithCache();
  };

  const fetchCancelReasonDataWithCache = useCallback(async () => {
    if (!effectiveHospitalId) return;
    setError(null);
    const loadingTimeout = setTimeout(() => {
      setIsLoading(true);
    }, 500);
    const cacheKey = `cancelReasonData-${effectiveHospitalId}-${getISODateFromDate(effectiveStartDate)}-${getISODateFromDate(effectiveEndDate)}`;
    const cachedData = CacheWithExpiration.get(cacheKey);
    if (cachedData) {
      setCancelReasonData(cachedData);
      clearTimeout(loadingTimeout);
      setIsLoading(false);
      return;
    }
    try {
      const data = await getCancelreasonAnalytics({
        hospital_id: effectiveHospitalId,
        start_date: effectiveStartDate.toISOString().split("T")[0],
        end_date: effectiveEndDate.toISOString().split("T")[0],
        doctor_id: userData?.doctor_id
      });
      CacheWithExpiration.set(cacheKey, data, 60);
      setCancelReasonData(data);
    } catch (error) {
      setError("Failed to load chart data. Please try again.");
    } finally {
      clearTimeout(loadingTimeout);
      setIsLoading(false);
    }
  }, [effectiveHospitalId, effectiveStartDate, effectiveEndDate, userData]);

  const handleDownloadCancelReason = async () => {
    if (!effectiveHospitalId) return;
    const params = {
      hospital_id: effectiveHospitalId,
      start_date: format(effectiveStartDate, 'yyyy-MM-dd'),
      end_date: format(effectiveEndDate, 'yyyy-MM-dd'),
      doctor_id: userData?.doctor_id
    };
    try {
      const response = await downloadCancelReasonAnalytics(params);
      if (onDownloadRequested) {
        onDownloadRequested(response);
      } else {
        toast.success("Download request submitted successfully");
      }
    } catch (e: any) {
      toast.error(e?.message || "Failed to request download");
    }
  };

  // Fetch data
  useEffect(() => {
    if (cancelStats) {
      setCancelReasonData(cancelStats);
    } else if (userData && effectiveHospitalId && effectiveStartDate && effectiveEndDate) {
      fetchCancelReasonDataWithCache();
    }
  }, [effectiveHospitalId, effectiveStartDate, effectiveEndDate, userData, cancelStats, fetchCancelReasonDataWithCache]);

  const pieChartOptions: ApexOptions = {
    chart: {
      type: "polarArea",
      height: report?200:700,
      width: "100%",
    },
    labels: [
      "R1: Feeling better",
      "R2: Work conflict",
      "R3: Emergency",
      "R4: Travel issue",
      "R5: Wrong booking",
      "Other reason",
    ],
    responsive: [
      {
        breakpoint: 480,
        options: {
          chart: {
            // width: 350, // Removed for dynamic adjustment
          },
          legend: {
            position: "bottom",
          },
        },
      },
    ],
    legend: {
      position: "right",
      itemMargin: {
        horizontal: 5,
        vertical: 5,
      },
      fontSize: "12px",
    },
    colors: [
      "#FFC107",
      "#4CAF50",
      "#2196F3",
      "#E91E63",
      "#9C27B0",
      "#00BCD4",
      "#FF5722",
    ],
    stroke: {
      colors: ["#fff"],
    },
    fill: {
      opacity: 0.8,
    },
    tooltip: {
      y: {
        formatter: function (val: number) {
          return val + " cancellation";
        },
      },
    },
    plotOptions: {
      polarArea: {
        rings: {
          strokeWidth: 1,
        },
        spokes: {
          strokeWidth: 1,
        },
      },
    },
    yaxis: {
      show: true, // Hide the y-axis labels (radial numbers)
    },
  };

  const pieChartSeries = cancelReasonData?.map((item) => item.count);

  return (
    <div className={`w-full ${report ? "w-full" : "lg:w-1/2"} bg-white rounded-lg shadow flex flex-col justify-center p-4`}>
      {/* chart title and date range selector */}
      <div className="flex flex-col gap-2 sm:flex-row justify-between items-center p-2 pt-0 mb-4">
        <h2 className="hidden md:flex text-lg font-semibold text-gray-700 ">
          Cancellation Reasons
        </h2>
        <div className="flex md:hidden flex-row justify-between w-full">
        <h2 className="flex text-lg font-semibold text-gray-700 ">
          Cancellation Reasons
        </h2>
        <IoMdDownload className="flex w-8 h-8 text-[#474747] hover:bg-gray-300 p-1 rounded-md cursor-pointer" onClick={handleDownloadCancelReason} />
        </div>
        <div className={`flex gap-4 ${report ? "hidden" : ""}`}>
          <DateRangePicker
            startDate={effectiveStartDate}
            endDate={effectiveEndDate}
            onStartChange={handleStartDateChange}
            onEndChange={handleEndDateChange}
            size="md"
            dateFormat="MMM dd, yyyy"
            inputClassName="border-blue-300 focus:ring-blue-600 text-sm py-1"
            separator="to"
            showIcons={false}
            maxDate={getDateBeforeXDays(1)}
            minDate={new Date('2023-01-01')}
          />
          <IoMdDownload className="hidden md:flex w-8 h-8 text-[#474747] hover:bg-gray-300 p-1 rounded-md cursor-pointer" onClick={handleDownloadCancelReason} />
        </div>
      </div>

      {/* Data Loading State */}
      {isLoading && (
        <ChartLoader height={350}  containerClassName="" />
      )}

      {/* Error State */}
      {error && !isLoading && (
        <ChartError
          error={error}
          onRetry={handleRetry}
          containerClassName="border-t border-gray-100"
          height={350}
        />
      )}

      {/* Chart Content */}
      {!isLoading && !error && (
        <>
          {pieChartSeries && pieChartSeries.length > 0 && pieChartSeries.some(value => value > 0) ? (
            <ReactApexChart
              options={pieChartOptions}
              series={pieChartSeries}
              type="polarArea"
              height={pieChartOptions.chart?.height}
            />
          ) : (
            <NoDataState
              title="No Cancellation Data Available"
              description="No cancellation data found for the selected date range. Try adjusting your date range."
              icon={<FiXCircle className="w-16 h-16 text-red-400" />}
              className="py-8"
              iconContainerClassName="bg-gradient-to-br from-red-50 to-red-100 rounded-full p-6 mb-4"
            />
          )}
        </>
      )}
    </div>
  );
}

export default CancelledReasonGraph;
